import _ from 'lodash';

import client from './RestClient';
import { Order } from '../core/order/Order';
import { Pagination } from 'core/pagination/Pagination';
import { Pageable } from './Pageable';
import { AxiosInstance } from 'axios';
import { CampaignBidPrice } from 'core/campaignBidPrice/CampaignBidPrice';
import { CampaignBidPriceWebService, RestfulCampaignBidPriceWebService } from './CampaignBidPriceWebService';
import { DefaultCampaignBidPriceManager } from 'core/campaignBidPrice/CampaignBidPriceManager';

export interface OrderWebService {
  getOrder (orderNumber: string): Promise<Order>;
  getOrderById (orderId: string | number): Promise<Order>;
  getOrders (advertiserId: number | null | undefined,
    pageable: Pageable,
    search?: string,
    deliveries?: string[]): Promise<OrdersWithPagination>;
  approveOrder (orderId: number): Promise<void>;
  rejectOrder (orderId: number): Promise<void>;
  createOrder (order: Omit<Order, 'id'>): Promise<string>;
  updateOrder (order: Order): Promise<string>;
  copyOrder (orderId: number, order: CopyOrderRequest): Promise<string>;
  settleOrder (orderId: number): Promise<void>;
  getOrderName (orderId: number): Promise<string | null>;
}

export type OrdersWithPagination = {
  pagination: Pagination,
  orders: Array<Order>
};

export type CopyOrderRequest = Pick<Order, 'projectName' | 'startDate' | 'endDate' | 'budget' | 'comments'>;

function wrapCampaignConstraint (json: any): any {
  return {
    budgetMinimum: _.get(json, 'budgetMinimum'),
    campaignBudgetMinimum: _.get(json, 'campaignBudgetMinimum')
  };
}

function wrapBidPriceDataOfAdTypes (json: any, defaultCampaignBidPrices: CampaignBidPrice[]): any {
  if (!json) {
    return _.cloneDeep(defaultCampaignBidPrices);
  }
  const result: any[] = [];
  const campaignBidPriceManager = new DefaultCampaignBidPriceManager();
  const bidPriceSetting = campaignBidPriceManager.mergeCampaignBidPrices(defaultCampaignBidPrices, json);
  bidPriceSetting.forEach(bidPriceData => {
    let bidPriceDataResult: any = {
      type: _.get(bidPriceData, 'type')
    };
    bidPriceDataResult.autoBidCap = wrapBidPriceData(bidPriceData.autoBidCap);
    bidPriceDataResult.bidFloor = wrapBidPriceData(bidPriceData.bidFloor);
    result.push(bidPriceDataResult);
  });
  return result;
}

function wrapBidPriceData (json: any): any {
  if (!json) {
    return {};
  }
  let bidFloorResult = {};
  Object.keys(json).forEach((key) => {
    const resultKey = key.toLowerCase();
    bidFloorResult[resultKey] = json[key];
  });
  return bidFloorResult;
}

function wrapOrder (json: any, defaultCampaignBidPrices?: CampaignBidPrice[]): any {
  return _.omitBy(
    {
      id: _.get(json, 'orderId'),
      agencyId: _.get(json, 'agencyId'),
      projectName: _.get(json, 'projectName'),
      orderNumber: _.get(json, 'orderNumber'),
      advertiserId: _.get(json, 'advertiserId'),
      budget: _.get(json, 'budget'),
      orderType: _.get(json, 'orderType'),
      originBudget: _.get(json, 'originBudget'),
      changeBudget: _.get(json, 'changeBudget'),
      externalId: _.get(json, 'externalId'),
      state: _.get(json, 'state'),
      spent: _.get(json, 'spent'),
      creator: _.get(json, 'creator'),
      creatorEmail: _.get(json, 'creatorEmail'),
      expectedSpent: _.get(json, 'expectedSpent'),
      actualSpent: _.get(json, 'actualSpent'),
      comments: _.get(json, 'comments'),
      startDate: _.get(json, 'startDate'),
      endDate: _.get(json, 'endDate'),
      createDate: _.get(json, 'createDate'),
      createDateTime: _.get(json, 'createDateTime'),
      campaignConstraint: wrapCampaignConstraint(
        _.get(json, 'campaignConstraint')
      ),
      budgetBalance: _.get(json, 'budgetBalance'),
      currency: _.get(json, 'currency'),
      timezone: _.get(json, 'timezone'),
      impres: _.get(json, 'impres', 0),
      clicks: _.get(json, 'clicks', 0),
      modifyReason: _.get(json, 'modifyReason'),
      rtbCampaignRunningStatus: _.get(json, 'rtbCampaignRunningStatus'),
      needAlert: _.get(json, 'needAlert'),
      externalType: _.get(json, 'externalType'),
      campaignBidPrice: defaultCampaignBidPrices
        ? wrapBidPriceDataOfAdTypes(
            _.get(json, 'campaignBidPrice'),
            defaultCampaignBidPrices
          )
        : undefined
    },
    _.isNull
  );
}

function wrapOrderDetail (json: any, defaultCampaignBidPrices: CampaignBidPrice[]): any {
  return wrapOrder(json, defaultCampaignBidPrices);
}

function wrapOrderList (json: any): any {
  return _.defaultTo(json, []).flatMap((json: any) =>
    wrapOrder(json)
  );
}

function wrapPagination (json: any): any {
  return {
    page: _.get(json, 'page', 1),
    size: _.get(json, 'size', 10),
    totalCount: _.get(json, 'totalCount', 0)
  };
}

export class RestfulOrderWebService implements OrderWebService {
  constructor (
    private restClient: AxiosInstance = client,
    private campaignBidPriceWebService: CampaignBidPriceWebService = new RestfulCampaignBidPriceWebService()
  ) {}

  async getOrder (orderNumber: string): Promise<Order> {
    const response = await this.restClient.get(
      `/v2/adsOrders/orderNumbers/${orderNumber}`
    );
    const defaultCampaignBidPrices = await this.campaignBidPriceWebService.getDefaultCampaignBidPrices();
    return wrapOrderDetail(response.data, defaultCampaignBidPrices);
  }

  async getOrderById (orderId: string | number): Promise<Order> {
    const response = await this.restClient.get(`/v2/adsOrders/${orderId}`);
    const defaultCampaignBidPrices = await this.campaignBidPriceWebService.getDefaultCampaignBidPrices();
    return wrapOrderDetail(response.data, defaultCampaignBidPrices);
  }

  async getOrders (
    advertiserId: number | null | undefined,
    pageable: Pageable,
    search: string = '',
    deliveries: string[] = []
  ): Promise<OrdersWithPagination> {
    let { page, sizePerPage, sort, direction } = pageable;
    sort = sort ? sort : 'remainingDay';
    direction = direction ? direction : 'desc';
    let response;
    let sortParam = sort === 'orderNumber' ? sort : `${sort},id`;
    let url = `/v2/adsOrders?search=${encodeURIComponent(
      search
    )}&page=${page}&size=${sizePerPage}&sort=${sortParam},${direction}&deliveries=${deliveries}`;
    url = advertiserId ? `${url}&advertiser_id=${advertiserId}` : url;
    response = await this.restClient.get(url);
    return {
      orders: wrapOrderList(response.data.records),
      pagination: wrapPagination(response.data.pagination)
    };
  }

  async approveOrder (orderId: number) {
    await this.restClient.put(`/v2/adsOrders/${orderId}/approve`);
  }

  async rejectOrder (orderId: number) {
    await this.restClient.put(`/v2/adsOrders/${orderId}/reject`);
  }

  async createOrder (order: Order) {
    const response = await this.restClient.post('/v2/adsOrders', order);
    return response.data.orderNumber;
  }

  async updateOrder (order: Order) {
    const response = await this.restClient.put('/v2/adsOrders', order);
    return response.data.orderNumber;
  }

  async copyOrder (orderId: number, order: CopyOrderRequest) {
    const response = await this.restClient.post(`/v2/adsOrders/${orderId}/copy`, order);
    return response.data.orderNumber;
  }

  async settleOrder (orderId: number) {
    await this.restClient.put(`/adsOrders/settle/${orderId}`);
  }

  async getOrderName (orderId: number): Promise<string | null> {
    const response = await this.restClient.get(`/v2/adsOrders/${orderId}/name`);
    return response.data.result;
  }
}
